/**
 * OP Sync Full Runs View
 *
 * Compact view for scheduled / manual full sync runs.
 * Owns its own maxRecords state, history query, and sync mutations.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  getOPSyncHistory,
  startOPSync,
  stopOPSync,
  type OPSyncDashboardStats,
} from '@/lib/api';
import { formatDateTime, Stat } from './shared';
import { FullRunHistoryTable } from './DataTables';

export function FullRunsView({
  entityType,
  stats,
}: {
  entityType: string;
  stats: OPSyncDashboardStats;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [maxRecords, setMaxRecords] = useState('');

  const historyQuery = useQuery({
    queryKey: ['op-sync', 'history'],
    queryFn: () => getOPSyncHistory(30),
    refetchInterval: 10000,
  });

  const syncMutation = useMutation({
    mutationFn: (max?: number) => startOPSync(entityType, max),
    onSuccess: (data) => {
      toast({
        title: 'Full run started',
        description: data.jobId
          ? `${data.totalRecords} records in ${data.totalChunks} chunks`
          : 'No pending records available',
      });
      queryClient.invalidateQueries({ queryKey: ['op-sync'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const stopMutation = useMutation({
    mutationFn: () => stopOPSync(entityType),
    onSuccess: () => {
      toast({ title: 'Run stopped' });
      queryClient.invalidateQueries({ queryKey: ['op-sync'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const history = (historyQuery.data?.history || []).filter(
    (j) => j.entityType === entityType && !j.dryRun,
  );
  const lastRun = history[0];
  const parsedMax = maxRecords ? parseInt(maxRecords, 10) : undefined;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-3 p-4">
          {/* Metrics row */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
            <Stat label="Pending" value={stats.pending} className="text-amber-600" />
            <Stat label="Dispatched" value={stats.dispatched} className="text-emerald-600" />
            <Stat label="Dead Letter" value={stats.deadLetter} className="text-destructive" />
            <Stat label="Last Run" value={lastRun?.status ?? 'None'} />
            {lastRun && (
              <span className="text-xs text-muted-foreground">
                {formatDateTime(lastRun.startedAt)}
              </span>
            )}
          </div>

          {/* Controls row */}
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="number"
              value={maxRecords}
              onChange={(e) => setMaxRecords(e.target.value)}
              placeholder="Max"
              className="h-8 w-20"
            />
            <Button
              size="sm"
              onClick={() => syncMutation.mutate(parsedMax)}
              disabled={syncMutation.isPending}
              className="h-8"
            >
              {syncMutation.isPending ? 'Starting…' : 'Start Full Run'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => stopMutation.mutate()}
              disabled={stopMutation.isPending}
              className="h-8"
            >
              {stopMutation.isPending ? 'Stopping…' : 'Stop'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <FullRunHistoryTable history={history} isLoading={historyQuery.isLoading} />
    </div>
  );
}
